import React, { useEffect, useState } from "react";

import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useDispatch, useSelector } from "react-redux";
import { fetchPlaylistById, selectPlaylistsById } from "./slice";
import NavMain from "../../app/NavMain";
import Layout from "../../ui/Layout";
import ScrollablePage from "../../ui/ScrollablePage";
import Paper from "../../ui/Paper";
import PropTypes from "prop-types";
import LoadingSpinner, { Backdrop } from "../../ui/LoadingSpinner";
import PaperTitle, { TitleText } from "../../ui/PaperTitle";
import { Action, Actions, Facts, Header, Overview } from "../../ui/PageHeader";
import Breadcrumbs from "../../ui/Breadcrumbs";
import IDCopy from "../../ui/IDCopy";
import ErrorMsg from "../../ui/ErrorMsg";
import Timestamp from "../../ui/Timestamp";
import { selectAdvancedMode } from "../settings/slice";
import AstrositeName from "../astrosites/AstrositeName";
import TableHeader from "../../ui/TableHeader";
import Table, { Cell, Row } from "../../ui/Table";
import TableHeaderRow from "../../ui/TableHeaderRow";
import RSOInfo from "../rsos/RSOInfo";
import SpaceOrganisation from "../rsos/SpaceOrganisation";
import { fetchSpaceOrgs, selectSpaceOrgs } from "../rsos/slice";
import TextFilter from "../filters/TextFilter";
import DropDownFilter from "../filters/DropDownFilter";
import { API_BASE_URL } from "../../app/config";
import { selectAuth } from "../iam/slice";
import Button from "../../ui/Button";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import { DateTime } from "luxon";
import Text from "../../ui/Text";
import StatusText from "./StatusText";
/**
 * Renders a playlist for a particular astropsite
 * Playlists are the list of tasks that the Astrosite runs through over the course of a night
 * Tasks can be of varying types
 * @returns {JSX.Element}
 * @constructor
 */
const Playlist = () => {
	const dispatch = useDispatch();
	const playlistsByID = useSelector(selectPlaylistsById);
	const { id } = useParams();
	const { token } = useSelector(selectAuth);

	// User setting to show more details such as id's
	const advancedMode = useSelector(selectAdvancedMode);

	const playlist = playlistsByID[id];
	const tasks = playlist?.schedule?.filter((p) => p?.task?.type != "idle");

	function downloadTasksCSVFile(id) {
		// Create a temporary link element
		const link = document.createElement("a");
		link.href = `${API_BASE_URL}/api/playlists/${id}/tasks/csv${
			token ? "?token=" + encodeURIComponent(token) : ""
		}`;
		link.target = "_blank"; // Open the download in a new tab

		// Trigger the download
		link.click();
	}

	useEffect(() => {
		dispatch(fetchPlaylistById(id));
		dispatch(fetchSpaceOrgs());
	}, [id]);

	useEffect(() => {
		const intervalId = setInterval(() => {
			dispatch(fetchPlaylistById(id));
			dispatch(fetchSpaceOrgs());
		}, 5000);

		// Clear the interval if status is not deploying
		if (
			playlist?.scheduler_status !== "pending" &&
			playlist?.deployment_status !== "pending"
		) {
			clearInterval(intervalId);
		}

		// Cleanup the interval on component unmount
		return () => clearInterval(intervalId);
	}, [playlist]);

	return (
		<Layout>
			<NavMain />
			<Helmet>
				<title>Schedule {id} - Astrosite</title>
			</Helmet>
			<ScrollablePage>
				{/*only show page loading on initial load afterwards use the schedule and deploy statuses*/}
				{playlist?._loadStatus === "loading" &&
					playlist?.scheduler_status !== "pending" &&
					playlist?.deployment_status !== "pending" && (
						<Backdrop>
							<LoadingSpinner />
						</Backdrop>
					)}
				<Header>
					<Overview>
						<h1>Schedule</h1>
						<Breadcrumbs>
							<Link to="/playlists">Schedules</Link>
							<IDCopy id={id} showCopy />
						</Breadcrumbs>
					</Overview>
					<Facts>
						<dt>Astrosite ID</dt>
						<dd>
							<AstrositeName astrositeId={playlist?.astrosite_id} />
						</dd>
						<dt>Task count</dt>
						<dd>{tasks?.length}</dd>
						<dt>Created at</dt>
						<dd>
							{playlist?.created_at && (
								<>
									<Timestamp utc={advancedMode} columnMode={false}>
										{playlist?.created_at}
									</Timestamp>
									<span>
										{DateTime.fromISO(playlist?.created_at).toRelative()}
									</span>
								</>
							)}
						</dd>
						<dt>Starting at</dt>
						<dd>
							{tasks?.length > 0 && (
								<Timestamp utc={advancedMode} columnMode={false}>
									{tasks[0].begin}
								</Timestamp>
							)}
						</dd>
					</Facts>
					<Facts>
						<dt>Schedule generated</dt>
						<dd>
							<StatusText status={playlist?.scheduler_status} />
						</dd>
						<dt>Deployment status</dt>
						<dd>
							<StatusText status={playlist?.deployment_status} />
						</dd>
						<dt>User</dt>
						<dd>{playlist?.created_by}</dd>
					</Facts>
					<Actions>
						<Action>
							<Button
								icon={faDownload}
								onClick={() => downloadTasksCSVFile(id)}
							>
								Export as CSV
							</Button>
						</Action>
					</Actions>
				</Header>
				{playlist?._loadStatus === "rejected" && (
					<ErrorMsg
						msg={`There was a problem fetching schedule with ID ${id}`}
						showIcon={true}
					/>
				)}
				{playlist?.scheduler_status === "pending" && (
					<Paper>
						<PaperTitle>
							<TitleText loadingState={"loading"}>
								Generating tasks for schedule
							</TitleText>
						</PaperTitle>
					</Paper>
				)}
				{!playlist?.deployment_status === "pending" && (
					<Paper>
						<PaperTitle>
							<TitleText loadingState={"loading"}>
								Sending schedule to Astrosite
							</TitleText>
						</PaperTitle>
					</Paper>
				)}
				{playlist?.scheduler_status !== "pending" && (
					<TaskList tasks={tasks} status={playlist?._loadStatus} />
				)}
			</ScrollablePage>
		</Layout>
	);
};

/**
 * Renders a table of tasks for this playlist
 * @param tasks
 * @returns {JSX.Element}
 * @constructor
 */
const TaskList = ({ tasks = [] }) => {
	// User setting to show more details such as id's
	const advancedMode = useSelector(selectAdvancedMode);
	const spaceOrgs = useSelector(selectSpaceOrgs); // Space organisations

	const [filter, setFilter] = useState({});

	const filteredTasks = tasks.filter((task) => {
		let objectMatch = true;
		if (filter?.object_cnts) {
			objectMatch =
				task?.task?.object_name
					?.toLowerCase()
					.includes(filter?.object_cnts?.toLowerCase()) ||
				task?.task?.id
					?.toLowerCase()
					.includes(filter?.object_cnts?.toLowerCase());
		}
		let spaceOrgMatch = true;
		if (filter?.org_code) {
			spaceOrgMatch = task?.task?.object_country
				?.toLowerCase()
				.includes(filter?.org_code?.toLowerCase());
		}

		return objectMatch && spaceOrgMatch;
	});

	return (
		<Paper>
			<PaperTitle>
				<TitleText>
					Showing {filteredTasks?.length} of {tasks?.length} scheduled tasks
				</TitleText>
			</PaperTitle>

			<Table>
				<TableHeaderRow sticky={true}>
					{advancedMode && <TableHeader>ID</TableHeader>}
					<TableHeader>Type</TableHeader>
					<TableHeader
						filterControl={
							<TextFilter
								filterByName="object_cnts"
								filter={filter}
								setFilter={setFilter}
								placeholder="name or norad id"
								active={filter.object_cnts ? true : false}
								textEntryDebounceMs={100}
								showClear={false}
							/>
						}
					>
						Object
					</TableHeader>
					<TableHeader>Start</TableHeader>
					<TableHeader
						filterControl={
							<DropDownFilter
								values={
									spaceOrgs &&
									spaceOrgs.map((so) => {
										return { name: so.name, value: so.code };
									})
								}
								filterByName="org_code"
								filter={filter}
								setFilter={setFilter}
								active={filter.org_code ? true : false}
							/>
						}
						// sortControl={
						//   filter && (
						//     <SortHeader
						//       setFilter={setFilter}
						//       filter={filter}
						//       sortBy={"org_code"}
						//     />
						//   )
						// }
					>
						Space Organisation
					</TableHeader>
				</TableHeaderRow>
				{tasks && (
					<tbody>
						{filteredTasks.map((task, i) => (
							<Row key={i} zebra>
								{advancedMode && <Cell>{task?.task?.task_id}</Cell>}

								<Cell>{task?.task?.type}</Cell>
								<Cell>
									<RSOInfo
										id={task?.task?.id}
										object_name={task?.task?.object_name}
										object_type={task?.task?.object_type}
									/>
								</Cell>
								<Cell>
									<span>{DateTime.fromISO(task?.begin).toRelative()}</span>
									<Timestamp utc={advancedMode} columnMode={false}>
										{task?.begin}
									</Timestamp>
								</Cell>
								<Cell>
									<SpaceOrganisation code={task?.task?.object_country} />
								</Cell>
							</Row>
						))}
					</tbody>
				)}
			</Table>
		</Paper>
	);
};
TaskList.propTypes = {
	tasks: PropTypes.array,
};

const PlaylistStatus = ({ status }) => (
	<Text
		success={status == "success"}
		secondary={status != "success" && status != "none"}
	>
		{status}
	</Text>
);

PlaylistStatus.propTypes = {
	status: PropTypes.string,
};

Playlist.propTypes = {
	playlistID: PropTypes.string,
};

export default Playlist;
